import fileSize from "filesize";
import React from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import { attachmentsManager } from "../../../../../shared/core/service/services";
import type { Attachment } from "../../../../../shared/domains/accounting-transaction/transaction/attachment";
import { AttachmentsLinks } from "../../../../../shared/domains/accounting-transaction/transaction/attachments-links";
import type { AttachmentContent } from "../../../../../shared/domains/accounting-transaction/transaction/attachments-service";
import type { AccountingTransaction } from "../../../../../shared/domains/accounting-transaction/transaction/transaction";
import { useAttachmentLinks } from "../../../../../shared/domains/accounting-transaction/use-attachment-links";
import type { AuthorizationHold } from "../../../../../shared/domains/authorization-hold/authorization-hold";
import { getUrlFromLink } from "../../../../../shared/domains/BaseUrl";
import { useAsyncEffect } from "../../../../../shared/utils/utils";
import { theme } from "../../../styles/theme";

type AttachmentPreviewProps = {
  attachment: Attachment;
  transaction?: AccountingTransaction | AuthorizationHold;
};

export default function AttachmentPreview(props: AttachmentPreviewProps) {
  const { attachment, transaction } = props;
  const [content, setContent] = React.useState<AttachmentContent>();
  const { canGetContent } = useAttachmentLinks();
  const { formatMessage } = useIntl();

  useAsyncEffect(async () => {
    if (transaction && canGetContent(attachment)) {
      try {
        const url = getUrlFromLink(attachment.links, AttachmentsLinks.GetContent);
        const content = await attachmentsManager.getContent(transaction.id, attachment.id, url);
        if (content) {
          setContent(content);
        }
      } catch (e) {
        console.log(e);
      }
    }
  }, [attachment]);

  const contentType = content?.contentType;
  const fileType = contentType?.split("/")[0];
  const extention = contentType?.split("/")[1].split(";")[0] || "";
  const base64Img = content?.base64Img || "";

  return (
    <AttachmentContentStyled>
      <span>
        {formatMessage("registerScreen.pictureInputFilled")} {fileType} {extention ? `(${extention})` : ""}
      </span>
      {/* The weight reads as part of the name, on its line, not underneath it. */}
      {!!content && <AttachmentContentDescription>{fileSize(base64Img.length)}</AttachmentContentDescription>}
    </AttachmentContentStyled>
  );
}

const AttachmentContentStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 8px;
  width: 100%;
  ${theme.text};
  font-size: 15px;
  line-height: 20px;
  color: ${theme.colors.blue[800]};
`;

const AttachmentContentDescription = styled.span`
  color: ${theme.colors.gray[500]};
  text-transform: uppercase;
  font-size: 12px;
`;
